import { useRef, useState } from "react";
import { toast } from "sonner";
import { api } from "../lib/apiClient";
import { X, Download, Check } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const CDN = "https://static.prod-images.emergentagent.com/jobs/5d950d92-6b67-4808-826a-961251c2afb8/images/";
const proxied = (file) => `${BACKEND}/api/proxy-image?url=${encodeURIComponent(CDN + file)}`;
const P = {
  exterior: proxied("d6a5faac6fcd422c0e18f3fbc9658e7817e27a8d237a85134a5514ed52715dd1.png"),
  foyer: proxied("082e788885acaa524c5db0f437d4ed782484a68109a6b86b5c2f0d8bbcde390f.png"),
  kitchen: proxied("21779fc58039bbc32978b5f56b82dc9a714075c001a23a6d350380b70fb3c91c.png"),
  living: proxied("d1452cbca255de4ff974e4e1215d25a49907c885d767e108c6670daccf3a149c.png"),
};

const TABS = [
  "Budget", "Approvals", "FF&E", "Purchase Orders", "Invoices",
  "Proposals", "Checklists & Assignments", "Time Tracking", "Mood Boards",
  "Design Schemes", "Notes", "Calendar", "Photos", "Product Library", "AI Assistant",
];

const SIGNATURE = [
  ["Smart Fetch", "Add FF&E and Product Library pieces straight from a URL — priced, specified and photographed automatically."],
  ["Mood Boards", "Pinterest-compatible boards with one-click paint palettes from Benjamin Moore, Farrow & Ball and Sherwin-Williams."],
  ["Synced Dashboards", "Designer, team and client views stay perfectly in sync, in real time."],
  ["Your Branding", "Customize your studio's logo and details on every proposal, invoice and PDF export."],
];

export default function MagazinePoster({ onClose }) {
  const pageRef = useRef(null);
  const [downloading, setDownloading] = useState(false);

  const IM = P;

  const download = async () => {
    setDownloading(true);
    try {
      const res = await api.get("/marketing/magazine-ad", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "workroom-magazine-ad.jpg";
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 2000);
      toast.success("Magazine ad saved (JPEG) — ready for email marketing.");
    } catch (e) {
      toast.error("Could not export the image. Please try again.");
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] bg-black/92 flex items-start justify-center p-4 overflow-auto" data-testid="mag-poster" onClick={onClose}>
      <div className="fixed top-5 right-6 z-30 flex items-center gap-3">
        <button data-testid="poster-download" onClick={(e) => { e.stopPropagation(); download(); }} disabled={downloading}
          className="flex items-center gap-2 bg-[#A68A6B] hover:bg-[#8C7154] text-white rounded-full px-4 py-2 text-xs uppercase tracking-wider shadow-lg disabled:opacity-60">
          <Download size={16} /> {downloading ? "Exporting…" : "Download for email (JPEG)"}
        </button>
        <button data-testid="poster-close" onClick={onClose}
          className="flex items-center gap-2 bg-white/95 hover:bg-white text-[#2B2622] rounded-full px-4 py-2 text-xs uppercase tracking-wider shadow-lg">
          <X size={16} /> Close
        </button>
      </div>

      <div ref={pageRef} className="w-full max-w-[760px] my-8 shadow-2xl" data-testid="poster-page" onClick={(e) => e.stopPropagation()} style={{ background: "#F4F0EA" }}>
        {/* Masthead */}
        <div className="px-12 pt-14 pb-6 text-center">
          <div className="w-16 h-px bg-[#C9BBAE] mx-auto mb-7" />
          <h1 className="font-serif text-[#2B2622]" style={{ fontSize: "3.4rem", lineHeight: 1.03 }}>
            Design, managed<br />beautifully.
          </h1>
          <p className="text-[#5C5651] mt-5 font-serif italic text-lg">Your admin studio workspace.</p>
        </div>

        {/* Hero */}
        <div className="px-12">
          <img src={IM.exterior} alt="" crossOrigin="anonymous" className="w-full block object-cover" style={{ aspectRatio: "16 / 10" }} />
          <p className="text-[10px] uppercase tracking-[0.32em] text-[#A68A6B] mt-3">Timeless Aesthetic · Streamlined Workflow · Considered Features</p>
        </div>

        {/* Photo strip */}
        <div className="px-12 pt-8 grid grid-cols-3 gap-2">
          {[IM.living, IM.kitchen, IM.foyer].map((src, i) => (
            <img key={i} src={src} alt="" crossOrigin="anonymous" className="w-full block object-cover" style={{ aspectRatio: "1 / 1" }} />
          ))}
        </div>

        {/* Everything in one place */}
        <div className="px-12 py-10">
          <div className="text-center">
            <p className="text-[11px] uppercase tracking-[0.35em] text-[#A68A6B] mb-4">Everything, in one place</p>
            <div className="w-10 h-px bg-[#C9BBAE] mx-auto mb-6" />
          </div>
          <div className="grid grid-cols-3 gap-x-6 gap-y-2.5">
            {TABS.map((t) => (
              <div key={t} className="flex items-center gap-2 text-sm text-[#2B2622]">
                <Check size={13} className="text-[#A68A6B] shrink-0" /> <span className="font-serif">{t}</span>
              </div>
            ))}
          </div>

          <div className="w-full h-px bg-[#E4DCD2] my-8" />

          <div className="grid grid-cols-2 gap-x-8 gap-y-6">
            {SIGNATURE.map(([title, body]) => (
              <div key={title}>
                <h3 className="font-serif text-lg text-[#2B2622]">{title}</h3>
                <p className="text-[13px] text-[#5C5651] leading-relaxed mt-1">{body}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Footer */}
        <div className="px-12 pb-12 pt-2 text-center border-t border-[#E4DCD2] mx-12">
          <h2 className="font-serif text-[#2B2622] text-3xl mt-8">WorkRoom</h2>
          <p className="text-[#5C5651] mt-3 uppercase tracking-[0.35em] text-xs">workroomadmin.com</p>
        </div>
      </div>
    </div>
  );
}
